/**
 * Cart Context
 *
 * Our own cart (replaces the Shopify cart). Lines live in localStorage; the
 * backend prices and validates them (`POST /api/storefront/cart/quote/`) and
 * turns them into a Stripe Checkout Session (`POST /api/storefront/cart/checkout/`).
 * Prices are never computed or trusted client-side.
 *
 * See docs/checkout/stripe-checkout.md for the contract.
 */

import { useRouter } from 'next/router'
import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'

import { setToast } from '@/entities/toast'
import { fbqTrack, GOOGLE_EVENTS, META_EVENTS, trackEcommerce, trackEvent } from '@/shared/analytics'
import { extractApiError } from '@/shared/api/extractApiError'
import { getStorefrontCatalogQueryKey } from '@/shared/api/generated/storefront-catalog/storefront-catalog'
import { storefrontCartCheckout, storefrontCartQuote } from '@/shared/api/generated/storefront-checkout/storefront-checkout'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { getEventDiscount } from '@/shared/hooks/useEventDiscount'
import { useAppDispatch } from '@/shared/store'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CartLineInput as ApiCartLineInput, CartLineKindEnum, CartQuote, StorefrontCatalog,
} from '@/shared/api/models'

// =============================================================================
// Types
// =============================================================================

export type CartLineKind = CartLineKindEnum

export interface CartLineInput {
  kind: CartLineKind
  plan_id?: number
  preset_id?: number
  story_id?: number
  quantity?: number
  /** Book added as part of the "Unlimited + Hardcover" bundle (local only, never sent to the API) */
  fromBundle?: boolean
}

export interface CartLine extends CartLineInput {
  key: string
  quantity: number
  /**
   * How many of this book line's units came from the "Unlimited + Hardcover"
   * bundle, so switching plans only touches those — never a book the shopper
   * added separately. Local only: not part of the key or the API payload.
   */
  bundleQuantity?: number
}

interface CartContextValue {
  lines: CartLine[]
  /** False until the cart has been restored from localStorage (first client render) */
  isReady: boolean
  /** Sum of line quantities */
  itemCount: number
  /** Server-priced quote for the current lines (undefined while empty/loading/failed) */
  quote: CartQuote | undefined
  isQuoteLoading: boolean
  /** Why the last quote request failed (network/5xx/validation); null when fine */
  quoteError: string | null
  retryQuote: () => void
  isCheckingOut: boolean
  /** Last checkout error, readable by any consumer */
  error: string | null
  /** Add a line (merges with an identical one); fires add_to_cart analytics */
  addItem: (line: CartLineInput, options?: { goToCart?: boolean }) => void
  removeItem: (key: string) => void
  setQuantity: (key: string, quantity: number) => void
  /** Remove one unit of the book that came with the "Unlimited + Hardcover" bundle */
  removeBundledBook: () => void
  clearCart: () => void
  hasItem: (match: Partial<CartLineInput>) => boolean
  /** Check out the whole cart. Not logged in → /cart login panel, then back to /cart. */
  checkout: () => Promise<void>
  /** Check out just these lines (cart untouched). Not logged in → lines go to the cart + login. */
  buyNow: (lines: CartLineInput[]) => Promise<void>
  /** Called by /checkout/success once the order is paid: removes exactly what that checkout bought. */
  completeCheckout: (sessionId: string) => void
}

// =============================================================================
// Storage helpers
// =============================================================================

const STORAGE_KEY = 'pixie_cart_v1'
const LEGACY_OWNER_KEY = 'pixie_cart_owner_v1'
const PENDING_CHECKOUT_KEY = 'pixie_cart_pending_checkout_v1'

const MAX_BOOK_QUANTITY = 10

/** The backend rejects quotes/checkouts with more lines than this. */
export const MAX_CART_LINES = 20

export const CART_LOGIN_PATH = '/cart?login=1'

interface PendingCheckout {
  sessionId: string
  source: 'cart' | 'buy_now'
  /** What was checked out, so completion removes exactly that (not newer additions). */
  lines: { key: string; quantity: number }[]
}

interface StoredCart {
  lines: CartLine[]
  /** User the cart belongs to; null for an anonymous cart */
  owner: string | null
}

const readStorage = (key: string): string | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string | null) => {
  try {
    if (typeof window === 'undefined') return

    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // Storage unavailable (private mode, quota) — the cart just won't persist.
  }
}

const KINDS: CartLineKind[] = ['software', 'unlimited', 'credit_pack', 'physical_book']

const clampQuantity = (quantity: number) =>
  Math.min(MAX_BOOK_QUANTITY, Math.max(1, Math.floor(quantity) || 1))

/** Deterministic key so re-adding the same product merges into one line. */
export const getCartLineKey = (line: CartLineInput): string =>
  [line.kind, line.plan_id ?? '', line.preset_id ?? '', line.story_id ?? ''].join(':')

const normalizeLine = (line: CartLineInput & { bundleQuantity?: number }): CartLine => {
  const normalized: CartLine = {
    key: '',
    kind: line.kind,
    quantity: line.kind === 'physical_book' ? clampQuantity(line.quantity ?? 1) : 1,
  }

  // Keep only the refs each kind uses, so equivalent lines share a key
  // (the backend ignores the rest: e.g. software/unlimited resolve their own plan).
  if (line.kind === 'credit_pack' && line.plan_id) normalized.plan_id = line.plan_id
  if (line.kind === 'physical_book' && line.preset_id) normalized.preset_id = line.preset_id
  if (line.kind !== 'credit_pack' && line.story_id) normalized.story_id = line.story_id

  if (line.kind === 'physical_book' && !line.story_id) {
    const bundled = line.fromBundle ? normalized.quantity : (line.bundleQuantity ?? 0)

    if (bundled > 0) normalized.bundleQuantity = Math.min(bundled, normalized.quantity)
  }

  normalized.key = getCartLineKey(normalized)

  return normalized
}

const parseStoredCart = (raw: string | null): StoredCart => {
  if (!raw) return { lines: [], owner: null }

  try {
    const parsed = JSON.parse(raw) as { lines?: CartLine[]; owner?: string | null }
    const lines = (parsed.lines ?? [])
      .filter((line) => line && KINDS.includes(line.kind))
      .map(normalizeLine)
      .slice(0, MAX_CART_LINES)

    return { lines, owner: parsed.owner ?? null }
  } catch {
    return { lines: [], owner: null }
  }
}

const toApiLine = (line: CartLine): ApiCartLineInput => ({
  key: line.key,
  kind: line.kind,
  ...(line.plan_id ? { plan_id: line.plan_id } : {}),
  ...(line.preset_id ? { preset_id: line.preset_id } : {}),
  ...(line.story_id ? { story_id: line.story_id } : {}),
  quantity: line.quantity,
})

/**
 * Merge a new line into the list. Story-unlock lines are exclusive: a story
 * gets either `software` or `unlimited`, and a cart holds at most one
 * `unlimited` line (the backend rejects a second one).
 */
const mergeLine = (lines: CartLine[], incoming: CartLine): CartLine[] => {
  let next = lines

  if (incoming.kind === 'unlimited') {
    next = next.filter((line) => line.kind !== 'unlimited')
  }

  if ((incoming.kind === 'software' || incoming.kind === 'unlimited') && incoming.story_id) {
    next = next.filter((line) => !(
      (line.kind === 'software' || line.kind === 'unlimited') && line.story_id === incoming.story_id
    ))
  }

  const existing = next.find((line) => line.key === incoming.key)

  if (!existing) return [...next, incoming]

  if (incoming.kind !== 'physical_book') return next

  return next.map((line) => {
    if (line.key !== incoming.key) return line

    const quantity = clampQuantity(line.quantity + incoming.quantity)
    const bundleQuantity = Math.min(quantity, (line.bundleQuantity ?? 0) + (incoming.bundleQuantity ?? 0))

    return { ...line, quantity, ...(bundleQuantity > 0 ? { bundleQuantity } : { bundleQuantity: undefined }) }
  })
}

const ITEM_NAMES: Record<CartLineKind, string> = {
  software: 'Single Story',
  unlimited: 'Unlimited Stories',
  credit_pack: 'Credit Pack',
  physical_book: 'Printed Storybook',
}

const toEcommerceItem = (line: CartLine) => ({
  item_id: line.story_id
    ? `${line.kind}-story-${line.story_id}`
    : line.plan_id ? `${line.kind}-plan-${line.plan_id}` : line.preset_id ? `${line.kind}-preset-${line.preset_id}` : line.kind,
  item_name: ITEM_NAMES[line.kind],
  item_category: line.kind,
  quantity: line.quantity,
})

const getHttpStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status

// =============================================================================
// Context
// =============================================================================

const CartContext = createContext<CartContextValue | null>(null)

interface CartProviderProps {
  children: ReactNode
}

export function CartProvider({ children }: CartProviderProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { isAuthenticated, profile } = usePixieWorldAuthContext()

  const [lines, setLines] = useState<CartLine[]>([])
  const [cartOwner, setCartOwner] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Every mutation goes through `updateLines`, which applies the change to a
  // ref synchronously. Handlers that call several operations in one tick
  // (e.g. the plan switcher: remove + add) then see each other's changes, and
  // side effects (analytics, notices) run once, outside React's updaters.
  const linesRef = useRef<CartLine[]>([])

  const updateLines = useCallback((updater: (prev: CartLine[]) => CartLine[]) => {
    const next = updater(linesRef.current)

    if (next !== linesRef.current) {
      linesRef.current = next
      setLines(next)
    }

    return next
  }, [])

  const notify = useCallback((title: string, content: string, severity: 'info' | 'warning' = 'info') => {
    dispatch(setToast({ open: true, severity, title, content }))
  }, [dispatch])

  // Restore on mount (client only, so SSR markup matches the empty cart).
  useEffect(() => {
    const stored = parseStoredCart(readStorage(STORAGE_KEY))

    updateLines(() => stored.lines)
    setCartOwner(stored.owner)
    setHydrated(true)
    writeStorage(LEGACY_OWNER_KEY, null)
  }, [updateLines])

  // Persist every change once restored. The owner lives with the lines so an
  // anonymous cart never carries a stale owner.
  useEffect(() => {
    if (!hydrated) return

    writeStorage(STORAGE_KEY, lines.length ? JSON.stringify({ lines, owner: cartOwner }) : null)
  }, [lines, cartOwner, hydrated])

  // Keep tabs in sync.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return

      const stored = parseStoredCart(event.newValue)

      updateLines(() => stored.lines)
      setCartOwner(stored.owner)
    }

    window.addEventListener('storage', onStorage)

    return () => window.removeEventListener('storage', onStorage)
  }, [updateLines])

  // Ownership:
  // - Logged in: a cart owned by someone else is cleared; an anonymous cart is adopted.
  // - Logged out: story-bound lines belonged to the previous user, so they go,
  //   and the rest becomes an anonymous cart.
  // `isAuthenticated` without a profile means the profile is still loading — wait.
  const ownerId = profile ? String(profile.id ?? profile.email) : null

  useEffect(() => {
    if (!hydrated) return

    if (ownerId) {
      if (cartOwner && cartOwner !== ownerId) {
        updateLines(() => [])
      }

      if (cartOwner !== ownerId) setCartOwner(ownerId)

      return
    }

    if (isAuthenticated || !cartOwner) return

    updateLines((prev) => prev.filter((line) => !line.story_id))
    setCartOwner(null)
  }, [hydrated, ownerId, isAuthenticated, cartOwner, updateLines])

  // ---------------------------------------------------------------------------
  // Quote
  // ---------------------------------------------------------------------------

  const apiLines = useMemo(() => lines.map(toApiLine), [lines])

  const quoteQuery = useQuery({
    // Auth changes the quote (story ownership, first-order discount).
    queryKey: ['/api/storefront/cart/quote/', apiLines, ownerId],
    queryFn: ({ signal }) => storefrontCartQuote({ lines: apiLines }, undefined, signal),
    enabled: hydrated && apiLines.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    // Retry transient failures once; a 4xx won't get better by retrying.
    retry: (failureCount, err) => {
      const status = getHttpStatus(err)

      return failureCount < 1 && (!status || status >= 500)
    },
  })

  const quote = lines.length && !quoteQuery.isError ? quoteQuery.data : undefined
  const quoteError = lines.length && quoteQuery.isError && !quoteQuery.isFetching
    ? extractApiError(quoteQuery.error, 'We couldn\'t price your cart right now.')
    : null
  const { refetch: refetchQuote } = quoteQuery

  const retryQuote = useCallback(() => {
    refetchQuote()
  }, [refetchQuote])

  // Adopt the server's canonical lines. The backend resolves a book's preset
  // when none was given (story preset or catalog default) and merges lines
  // that turn out to be the same product, e.g. an unassigned book added once
  // without a preset and once with the default preset. Merged-away lines are
  // missing from the quote, so without this they'd linger in the cart unpriced.
  useEffect(() => {
    const data = quoteQuery.data

    if (!data || quoteQuery.isPlaceholderData) return

    updateLines((prev) => {
      const prevByKey = new Map(prev.map((line) => [line.key, line]))
      const quoteKeys = new Set(data.lines.map((line) => line.key))

      // Only trust a quote that was priced for exactly these lines.
      if (data.lines.some((line) => !line.key || !prevByKey.has(line.key))) return prev

      const needsCanonical = prev.some((line) => !quoteKeys.has(line.key))
        || data.lines.some((line) => (
          line.kind === 'physical_book' && !line.error && line.preset_id
            && !prevByKey.get(line.key as string)?.preset_id
        ))

      if (!needsCanonical) return prev

      const mergedAway = prev.filter((line) => !quoteKeys.has(line.key))

      return data.lines.reduce<CartLine[]>((acc, quoteLine) => {
        const line = prevByKey.get(quoteLine.key as string) as CartLine
        const isValidBook = line.kind === 'physical_book' && !quoteLine.error
        // Bundle units of lines merged into this one stay tagged.
        const extraBundle = isValidBook
          ? mergedAway.filter((other) => other.kind === 'physical_book' && other.story_id === line.story_id)
            .reduce((sum, other) => sum + (other.bundleQuantity ?? 0), 0)
          : 0

        return mergeLine(acc, normalizeLine({
          ...line,
          preset_id: line.preset_id ?? (isValidBook ? quoteLine.preset_id ?? undefined : undefined),
          quantity: isValidBook ? quoteLine.quantity : line.quantity,
          bundleQuantity: (line.bundleQuantity ?? 0) + extraBundle,
        }))
      }, [])
    })
  }, [quoteQuery.data, quoteQuery.isPlaceholderData, updateLines])

  // ---------------------------------------------------------------------------
  // Line operations
  // ---------------------------------------------------------------------------

  /** Unassigned books without a preset get the catalog default when we already have it cached. */
  const withDefaultPreset = useCallback((line: CartLineInput): CartLineInput => {
    if (line.kind !== 'physical_book' || line.preset_id || line.story_id) return line

    const catalog = queryClient.getQueryData<StorefrontCatalog>(getStorefrontCatalogQueryKey())

    return catalog?.default_book_preset_id ? { ...line, preset_id: catalog.default_book_preset_id } : line
  }, [queryClient])

  /** Returns false (and tells the shopper why) when the cart would exceed the line limit. */
  const addLines = useCallback((inputs: CartLineInput[]): boolean => {
    const normalized = inputs.map((input) => normalizeLine(withDefaultPreset(input)))
    const prev = linesRef.current
    const next = normalized.reduce(mergeLine, prev)

    if (next.length > MAX_CART_LINES) {
      notify(
        'Your cart is full',
        `A cart can hold up to ${MAX_CART_LINES} different items. Check out or remove something to add more.`,
        'warning',
      )

      return false
    }

    // One Unlimited plan per order: adding one replaces any other (for another story).
    const replacedUnlimited = normalized.some((incoming) => incoming.kind === 'unlimited' && prev.some((line) => (
      line.kind === 'unlimited' && line.story_id !== incoming.story_id
    )))

    updateLines(() => next)
    setError(null)

    if (replacedUnlimited) {
      notify(
        'Unlimited plan replaced',
        'An order can include one Unlimited plan, so the Unlimited plan already in your cart was replaced. One plan unlocks all your stories.',
      )
    }

    trackEcommerce('add_to_cart', normalized.map(toEcommerceItem), undefined, 'USD')
    fbqTrack(META_EVENTS.ADD_TO_CART, {
      content_ids: normalized.map((line) => toEcommerceItem(line).item_id),
      content_type: 'product',
      currency: 'USD',
    })

    return true
  }, [notify, updateLines, withDefaultPreset])

  const addItem = useCallback((line: CartLineInput, options?: { goToCart?: boolean }) => {
    const added = addLines([line])

    if (added && options?.goToCart) router.push('/cart')
  }, [addLines, router])

  const removeItem = useCallback((key: string) => {
    const removed = linesRef.current.find((line) => line.key === key)

    if (!removed) return

    updateLines((prev) => prev
      .filter((line) => line.key !== key)
      // Removing the Unlimited plan breaks the bundle; its book becomes a regular book.
      .map((line) => (removed.kind === 'unlimited' && line.bundleQuantity ? { ...line, bundleQuantity: undefined } : line)))

    trackEcommerce('remove_from_cart', [toEcommerceItem(removed)], undefined, 'USD')
  }, [updateLines])

  const setQuantity = useCallback((key: string, quantity: number) => {
    updateLines((prev) => prev.map((line) => {
      if (line.key !== key || line.kind !== 'physical_book') return line

      const nextQuantity = clampQuantity(quantity)
      const bundleQuantity = Math.min(line.bundleQuantity ?? 0, nextQuantity)

      return { ...line, quantity: nextQuantity, bundleQuantity: bundleQuantity > 0 ? bundleQuantity : undefined }
    }))
  }, [updateLines])

  const removeBundledBook = useCallback(() => {
    updateLines((prev) => {
      const bundled = prev.find((line) => (line.bundleQuantity ?? 0) > 0)

      if (!bundled) return prev

      if (bundled.quantity <= 1) return prev.filter((line) => line.key !== bundled.key)

      return prev.map((line) => (line.key === bundled.key
        ? {
          ...line,
          quantity: line.quantity - 1,
          bundleQuantity: (line.bundleQuantity ?? 1) - 1 || undefined,
        }
        : line))
    })
  }, [updateLines])

  const clearCart = useCallback(() => {
    updateLines(() => [])
    setError(null)
  }, [updateLines])

  const hasItem = useCallback((match: Partial<CartLineInput>) => lines.some((line) => (
    Object.entries(match) as [keyof CartLineInput, CartLineInput[keyof CartLineInput]][]
  ).every(([field, value]) => value === undefined || line[field] === value)), [lines])

  // ---------------------------------------------------------------------------
  // Checkout
  // ---------------------------------------------------------------------------

  const goToLogin = useCallback(() => {
    router.push(CART_LOGIN_PATH)
  }, [router])

  // Guards against double submits while a checkout request is in flight.
  const checkoutInFlight = useRef(false)

  // Coming Back from Stripe restores this page from the back/forward cache
  // with "checking out" still set; reset it so the buttons work again.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return

      checkoutInFlight.current = false
      setIsCheckingOut(false)
    }

    window.addEventListener('pageshow', onPageShow)

    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const startCheckout = useCallback(async (checkoutLines: CartLine[], source: PendingCheckout['source']) => {
    if (!checkoutLines.length || checkoutInFlight.current) return

    checkoutInFlight.current = true
    setIsCheckingOut(true)
    setError(null)

    trackEcommerce('begin_checkout', checkoutLines.map(toEcommerceItem), undefined, 'USD')
    fbqTrack(META_EVENTS.INITIATE_CHECKOUT, {
      content_ids: checkoutLines.map((line) => toEcommerceItem(line).item_id),
      num_items: checkoutLines.reduce((sum, line) => sum + line.quantity, 0),
      currency: 'USD',
    })

    try {
      const eventDiscount = getEventDiscount()
      const response = await storefrontCartCheckout({
        lines: checkoutLines.map(toApiLine),
        promo_code: eventDiscount?.code ?? null,
      })

      const pending: PendingCheckout = {
        sessionId: response.session_id,
        source,
        lines: checkoutLines.map((line) => ({ key: line.key, quantity: line.quantity })),
      }

      writeStorage(PENDING_CHECKOUT_KEY, JSON.stringify(pending))
      trackEvent(GOOGLE_EVENTS.CHECKOUT_REDIRECT, { order_number: response.order_number })

      // Stay in the "checking out" state while the browser leaves for Stripe
      // (the pageshow handler resets it if the shopper comes Back).
      window.location.assign(response.checkout_url)
    } catch (err) {
      const status = getHttpStatus(err)
      const data = (err as { response?: { data?: { quote?: CartQuote } } } | null)?.response?.data

      checkoutInFlight.current = false
      setIsCheckingOut(false)

      if (status === 401) {
        setError('Please log in to check out.')
        goToLogin()

        return
      }

      // 400 carries the re-priced quote so the cart can show what's wrong.
      if (data?.quote && source === 'cart') {
        queryClient.setQueryData(['/api/storefront/cart/quote/', checkoutLines.map(toApiLine), ownerId], data.quote)
      }

      setError(extractApiError(err, 'We couldn\'t start checkout. Please try again.'))
    }
  }, [goToLogin, ownerId, queryClient])

  const checkout = useCallback(async () => {
    const current = linesRef.current

    if (!current.length) return

    if (!isAuthenticated) {
      goToLogin()

      return
    }

    await startCheckout(current, 'cart')
  }, [goToLogin, isAuthenticated, startCheckout])

  const buyNow = useCallback(async (inputs: CartLineInput[]) => {
    if (!inputs.length) return

    // Logged out: park the lines in the cart so they survive the login round-trip.
    if (!isAuthenticated) {
      if (addLines(inputs)) goToLogin()

      return
    }

    const normalized = inputs
      .map((input) => normalizeLine(withDefaultPreset(input)))
      .reduce(mergeLine, [] as CartLine[])

    await startCheckout(normalized, 'buy_now')
  }, [addLines, goToLogin, isAuthenticated, startCheckout, withDefaultPreset])

  const completeCheckout = useCallback((sessionId: string) => {
    const raw = readStorage(PENDING_CHECKOUT_KEY)

    if (!raw) return

    try {
      const pending = JSON.parse(raw) as Partial<PendingCheckout>

      if (pending.sessionId !== sessionId) return

      const bought = new Map((pending.lines ?? []).map((line) => [line.key, line.quantity]))

      // Remove exactly what was bought: books are decremented by the quantity
      // purchased, so copies added after checkout started (or in another tab) stay.
      updateLines((prev) => prev.flatMap((line) => {
        const boughtQuantity = bought.get(line.key)

        if (boughtQuantity === undefined) return [line]
        if (line.kind !== 'physical_book') return []

        const remaining = line.quantity - boughtQuantity

        if (remaining <= 0) return []

        const bundleQuantity = Math.min(line.bundleQuantity ?? 0, remaining)

        return [{ ...line, quantity: remaining, bundleQuantity: bundleQuantity > 0 ? bundleQuantity : undefined }]
      }))
    } catch {
      // Corrupt record — nothing to clear.
    }

    writeStorage(PENDING_CHECKOUT_KEY, null)
  }, [updateLines])

  const itemCount = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity, 0),
    [lines],
  )

  const value: CartContextValue = {
    lines,
    isReady: hydrated,
    itemCount,
    quote,
    isQuoteLoading: quoteQuery.isFetching,
    quoteError,
    retryQuote,
    isCheckingOut,
    error,
    addItem,
    removeItem,
    setQuantity,
    removeBundledBook,
    clearCart,
    hasItem,
    checkout,
    buyNow,
    completeCheckout,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

// =============================================================================
// Hook
// =============================================================================

export function useCart(): CartContextValue {
  const context = useContext(CartContext)

  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }

  return context
}
